import type {
  Wallet,
  WalletOption,
  DynamicWalletKey,
} from '@stripe-internal/wallet-connect-wrapper';
import type {NetworkType, SupportedCurrency} from 'src/v2/state/types';
import {EVM_NETWORKS} from 'src/types';
import {isEmbedded, isMobile} from 'src/utils/utils';
import {getNetworkTypeFromWalletKey} from 'src/v2/util/getWalletKey';

// Returns true to keep the option, false to remove it
type WalletFilter = (option: WalletOption) => boolean;

interface FilterContext {
  relevantNetworkTypes: NetworkType[];
  connectedTypes: Set<string>;
}

const WALLETS_UNSUPPORTED_IN_MOBILE_EMBED = ['Phantom', 'Coinbase Wallet'];

// Determines which network types are relevant for this session based on supported currencies
const getRelevantNetworkTypes = (
  supportedCurrencies: SupportedCurrency[],
): NetworkType[] => {
  const supportedNetworks = [
    ...new Set(supportedCurrencies.map((currency) => currency.currencyNetwork)),
  ];

  const networkTypes: NetworkType[] = [];

  // Check if EVM networks are supported
  if (supportedNetworks.some((network) => EVM_NETWORKS.includes(network))) {
    networkTypes.push('evm');
  }

  // Check if Solana is supported
  if (supportedNetworks.includes('solana')) {
    networkTypes.push('solana');
  }

  return networkTypes;
};

// WalletConnect only supports EVM; other providers support both EVM and Solana
const getNetworkTypeForWalletOption = (
  walletOption: WalletOption,
): NetworkType[] => {
  if (walletOption.provider === 'WalletConnect' && walletOption.key) {
    return ['evm'];
  }

  return ['evm', 'solana'];
};

const getConnectedWalletTypes = (connectedWallets: Wallet[]): Set<string> => {
  const connectedTypes = new Set<string>();

  connectedWallets.forEach((wallet) => {
    const provider = wallet.type;
    const networkType = getNetworkTypeFromWalletKey(
      wallet.key as DynamicWalletKey,
    );

    // Unique key for provider + network combination
    connectedTypes.add(`${provider}:${networkType}`);
  });

  return connectedTypes;
};

// --- Filter predicates ---

const filterUnsupportedInMobileEmbed = (): WalletFilter => {
  const isMobileEmbedded = isMobile() && isEmbedded();

  return (option) =>
    !(
      isMobileEmbedded &&
      WALLETS_UNSUPPORTED_IN_MOBILE_EMBED.includes(option.provider)
    );
};

const filterIrrelevantNetworks = (context: FilterContext): WalletFilter => {
  return (option) => {
    const allSupportedNetworks = getNetworkTypeForWalletOption(option);
    // Only consider networks that are supported by this session
    const relevantSupportedNetworks = allSupportedNetworks.filter((network) =>
      context.relevantNetworkTypes.includes(network),
    );

    // Skip wallets that don't support any relevant networks
    return relevantSupportedNetworks.length > 0;
  };
};

// Hide only if ALL relevant networks for the session are already connected
const filterAlreadyConnected = (context: FilterContext): WalletFilter => {
  return (option) => {
    const allSupportedNetworks = getNetworkTypeForWalletOption(option);
    const relevantSupportedNetworks = allSupportedNetworks.filter((network) =>
      context.relevantNetworkTypes.includes(network),
    );

    if (relevantSupportedNetworks.length === 0) {
      return true;
    }

    // Check which relevant networks are already connected
    const connectedRelevantNetworks = relevantSupportedNetworks.filter(
      (networkType) =>
        context.connectedTypes.has(`${option.provider}:${networkType}`),
    );

    return (
      connectedRelevantNetworks.length !== relevantSupportedNetworks.length
    );
  };
};

const buildFilters = (context: FilterContext): WalletFilter[] => [
  filterUnsupportedInMobileEmbed(),
  filterIrrelevantNetworks(context),
  filterAlreadyConnected(context),
];

/**
 * Filters wallet options based on device/embed constraints, network relevance,
 * and already-connected state.
 */
export const filterAvailableWalletOptions = (
  allWalletOptions: WalletOption[],
  connectedWallets: Wallet[],
  supportedCurrencies: SupportedCurrency[],
): WalletOption[] => {
  const context: FilterContext = {
    relevantNetworkTypes: getRelevantNetworkTypes(supportedCurrencies),
    connectedTypes: getConnectedWalletTypes(connectedWallets),
  };

  const filters = buildFilters(context);
  return allWalletOptions.filter((option) => filters.every((f) => f(option)));
};
